// event-row — renders a single RunEvent in the EventTimeline list.
// Each event type has its own visual treatment; errors auto-expand.
// token_delta is NOT rendered here — it is rolled up into the parent step
// liveTokens buffer instead.
// agent.decide.complete events show ref count chip + expandable LinkIcon strip.

import { useState } from "react";
import { ChevronDown, AlertCircle, CheckCircle2, XCircle, Clock } from "lucide-react";
import { useTranslations } from "use-intl";
import { cn } from "@/utils";
import {
  StatusChip,
  type StatusChipTone,
} from "@/components/ui/status-chip/status-chip";
import { UserCell } from "@/components/users/user-cell";
import { RefLinkStrip } from "./ref-link-strip";
import type { RunEvent, KbRef, KgRef } from "@/types/workflow.types";

// ── Event category chip colours ───────────────────────────────────────────────

function kindChip(eventType: string): React.ReactNode {
  const typeKey = normalizedEventType(eventType);
  const tone = eventTone(typeKey);
  if (typeKey.startsWith("run_")) {
    return <StatusChip tone={tone}>run</StatusChip>;
  }
  if (typeKey.startsWith("llm_") || typeKey === "step_text_complete") {
    return <StatusChip tone={tone}>llm</StatusChip>;
  }
  if (
    typeKey.startsWith("agent_") ||
    typeKey === "decision_made"
  ) {
    return <StatusChip tone={tone}>agent</StatusChip>;
  }
  if (typeKey.startsWith("checkpoint_")) {
    return <StatusChip tone={tone}>checkpoint</StatusChip>;
  }
  if (typeKey.startsWith("cost_")) {
    return <StatusChip tone={tone}>cost</StatusChip>;
  }
  if (typeKey.startsWith("step_failed") || typeKey === "run_failed") {
    return <StatusChip tone="failed">error</StatusChip>;
  }
  // step_started, step_completed, step_skipped, step_progress
  return <StatusChip tone={tone}>step</StatusChip>;
}

function eventTone(typeKey: string): StatusChipTone {
  if (
    typeKey.includes("fail") ||
    typeKey.includes("rejected") ||
    typeKey.includes("expired") ||
    typeKey.includes("cap_exceeded")
  ) {
    return "failed";
  }
  if (
    typeKey.includes("complete") ||
    typeKey.includes("approved") ||
    typeKey === "run_completed"
  ) {
    return "done";
  }
  if (typeKey.startsWith("checkpoint_") || typeKey.startsWith("cost_")) {
    return "warning";
  }
  return "queued";
}

function eventIcon(eventType: string): React.ReactNode {
  const typeKey = normalizedEventType(eventType);
  if (typeKey.includes("complete") || typeKey.includes("approved") || typeKey === "run_completed") {
    return <CheckCircle2 size={12} className="text-[var(--success)] shrink-0" aria-hidden />;
  }
  if (typeKey.includes("fail") || typeKey.includes("rejected") || typeKey.includes("expired")) {
    return <XCircle size={12} className="text-[var(--destructive)] shrink-0" aria-hidden />;
  }
  if (typeKey.includes("warn") || typeKey.includes("cap")) {
    return <AlertCircle size={12} className="text-[var(--warning)] shrink-0" aria-hidden />;
  }
  return <Clock size={12} className="text-[var(--muted-foreground)] shrink-0" aria-hidden />;
}

function normalizedEventType(eventType: string): string {
  return eventType.replace(/\./g, "_");
}

function formatEventTitle(eventType: string): string {
  return eventType.replace(/[_.]/g, " ");
}

function formatTimestamp(createdAt: string): string {
  try {
    return new Date(createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return createdAt;
  }
}

// ── Payload viewer ────────────────────────────────────────────────────────────

function PayloadViewer({ payload }: { payload: Record<string, unknown> }) {
  // Redact token_delta payloads from logs — never log raw LLM output to console.
  const safe = { ...payload };
  delete safe.delta;
  delete safe.text;
  return (
    <pre className="text-[10px] leading-relaxed font-mono bg-[var(--muted)] rounded-[var(--radius-md)] px-2 py-1.5 overflow-x-auto custom-scrollbar whitespace-pre-wrap break-words max-h-[180px] overflow-y-auto">
      {JSON.stringify(safe, null, 2)}
    </pre>
  );
}

// ── Main row component ────────────────────────────────────────────────────────

export interface EventRowProps {
  event: RunEvent;
  /** Pre-expand error rows automatically so operators see failures immediately. */
  autoExpand?: boolean;
  systemActorLabel: string;
}

export function EventRow({ event, autoExpand = false, systemActorLabel }: EventRowProps) {
  const t = useTranslations("workflows");
  const typeKey = normalizedEventType(event.eventType);
  const isError =
    typeKey.includes("fail") ||
    typeKey.includes("rejected") ||
    typeKey.includes("expired") ||
    typeKey.includes("cap_exceeded");

  const [expanded, setExpanded] = useState(autoExpand || isError);

  const data = event.payload as Record<string, unknown>;

  // step_text_complete — expandable full-text row
  const isTextComplete = typeKey === "step_text_complete";
  const text = isTextComplete ? String(data.text ?? data.full_text ?? "") : null;

  // decision_made — show chosen label inline
  const isDecision = typeKey === "decision_made";
  const decisionLabel = isDecision ? String(data.label ?? data.branchLabel ?? "") : null;

  // agent_tool_call — show tool name + status
  const isToolCall = typeKey === "agent_tool_call";
  const toolName = isToolCall ? String(data.toolName ?? data.tool_name ?? "") : null;
  const toolStatus = isToolCall ? String(data.status ?? "called") : null;

  // checkpoint events — show status
  const isCheckpoint = typeKey.startsWith("checkpoint_");

  // agent.decide.complete — KB/KG ref count chip + expandable ref strip
  const isAgentDecideComplete = event.eventType === "agent.decide.complete";
  const kbRefs = isAgentDecideComplete && Array.isArray(data.kbRefs)
    ? (data.kbRefs as KbRef[])
    : undefined;
  const kgRefs = isAgentDecideComplete && Array.isArray(data.kgRefs)
    ? (data.kgRefs as KgRef[])
    : undefined;
  const hasRefs = !!(kbRefs?.length || kgRefs?.length);
  const refCountLabel = hasRefs
    ? [
        kbRefs?.length ? `${kbRefs.length} KB` : null,
        kgRefs?.length ? `${kgRefs.length} KG` : null,
      ].filter(Boolean).join(" · ")
    : null;

  return (
    <div
      className={cn(
        "flex flex-col gap-1 px-3 py-2 rounded-[var(--radius-md)]",
        "border border-transparent hover:border-[var(--border)] hover:bg-[var(--accent)]",
        "transition-colors duration-[120ms]",
        "animate-[slideDown_180ms_cubic-bezier(0.2,0,0,1)_both]",
        isError && "border-[var(--destructive-bg)] bg-[var(--destructive-bg)]"
      )}
    >
      {/* Row header */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-2 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] rounded-[var(--radius-sm)]"
      >
        {eventIcon(event.eventType)}
        {kindChip(event.eventType)}

        <span className="flex-1 min-w-0 text-[12px] font-medium text-[var(--foreground)] truncate capitalize">
          {formatEventTitle(event.eventType)}
          {decisionLabel && (
            <span className="ml-1.5 text-[var(--purple)] font-semibold">→ {decisionLabel}</span>
          )}
          {toolName && (
            <span className="ml-1.5 text-[var(--muted-foreground)] font-normal">{toolName}</span>
          )}
          {toolStatus && (
            <span className="ml-1 text-[11px] text-[var(--muted-foreground)]">({toolStatus})</span>
          )}
          {isCheckpoint && typeof data.title === "string" && data.title && (
            <span className="ml-1.5 text-[var(--muted-foreground)] font-normal text-[11px]">
              — {data.title}
            </span>
          )}
        </span>

        {/* KB/KG ref count chip — only on agent.decide.complete with refs */}
        {refCountLabel && (
          <span className="shrink-0 text-[11px] text-[var(--muted-foreground)] font-mono">
            · {refCountLabel}
          </span>
        )}

        {/* Actor */}
        {event.actorUser ? (
          <UserCell
            user={event.actorUser}
            size="xs"
            fallbackText={systemActorLabel}
            className="shrink-0 text-[11px]"
          />
        ) : (
          <span className="shrink-0 text-[11px] text-[var(--muted-foreground)]">
            {systemActorLabel}
          </span>
        )}

        <span className="shrink-0 text-[11px] text-[var(--muted-foreground)] font-mono">
          {formatTimestamp(event.createdAt)}
        </span>

        <ChevronDown
          size={12}
          strokeWidth={1.6}
          aria-hidden
          className={cn(
            "shrink-0 text-[var(--muted-foreground)] transition-transform duration-[180ms]",
            expanded ? "-rotate-180" : "rotate-0"
          )}
        />
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="pl-4 animate-[expand_180ms_cubic-bezier(0.2,0,0,1)_both]">
          {isTextComplete && text ? (
            <pre className="text-[11px] font-mono bg-[var(--muted)] rounded-[var(--radius-md)] px-2 py-1.5 whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto custom-scrollbar">
              {text}
            </pre>
          ) : (
            <PayloadViewer payload={data} />
          )}
          {isCheckpoint && typeof data.checkpointId === "string" && data.checkpointId && (
            <p className="mt-1 text-[11px] text-[var(--muted-foreground)]">
              {t("run.event.checkpoint_id")}: {data.checkpointId}
            </p>
          )}
          {/* Full LinkIcon ref strip — only on agent.decide.complete with refs */}
          <RefLinkStrip kbRefs={kbRefs} kgRefs={kgRefs} />
        </div>
      )}
    </div>
  );
}
